import type { LensCardView } from '~/types/leLens';

import { applyView, dateColumn, looksLikeDate } from '../cardView';
import { isNumeric } from '../lensChart';

export type MetricTrend = {
  /** The date column the points are grouped on. */
  dateColumn: string;
  /** The measure the points aggregate, the same column the headline shows. */
  column: string;
  /** Dates in ascending order, one per point. */
  dates: Array<string>;
  /** The aggregated measure per date, aligned with `dates`. */
  values: Array<number>;
};

/** Fewer points than this is not a trend, so no sparkline is drawn. */
export const METRIC_TREND_MIN_POINTS = 3;

/** A sparkline is a glance, not a chart: only the most recent points are kept. */
export const METRIC_TREND_MAX_POINTS = 60;

type CalculatedAggregates = Parameters<typeof applyView>[4];

function sortKey(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

/**
 * The trend behind a metric card's headline. A metric card reads the same
 * dataset rows its sibling charts do and reduces them to one number, so when
 * the dataset is keyed on a date the headline's own aggregate can be re-run
 * per date to show how the number got there. Nothing new is fetched: this is
 * the card's own rows, grouped one level finer than the headline.
 *
 * Returns null when the rows carry no date column, when the date column is
 * the measure itself, or when there are too few dates to read as a trend.
 */
export function resolveMetricTrend(
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
  view: LensCardView | undefined,
  aggregate: CalculatedAggregates,
  valueColumn: string,
  /** LensCardData.aggregated: the headline's own rows are already one per group for a governed
   *  card, so the per-date re-run below has to sum rather than re-apply Combine, the same rule
   *  applyView itself follows. */
  aggregated = false,
): MetricTrend | null {
  if (!rows.length || !columns.length) {
    return null;
  }
  const x = view?.x && looksLikeDate(rows, view.x) ? view.x : dateColumn(rows, columns);
  if (!x || x === valueColumn) {
    return null;
  }

  const shaped = applyView(
    rows,
    columns,
    { ...(view ?? {}), x, series: undefined, values: undefined, value: view?.value ?? valueColumn },
    'line',
    aggregate,
    aggregated,
  );
  if (!shaped.rows?.length || !shaped.columns?.length) {
    return null;
  }
  const column =
    shaped.columns.find((name) => name === valueColumn) ??
    shaped.columns.find((name) => name !== x && shaped.rows.some((row) => isNumeric(row[name])));
  if (!column) {
    return null;
  }

  const points = shaped.rows
    .filter((row) => row[x] !== null && row[x] !== undefined && isNumeric(row[column]))
    .map((row) => ({ date: String(row[x]), value: Number(row[column]) }))
    .sort((a, b) => sortKey(a.date) - sortKey(b.date))
    .slice(-METRIC_TREND_MAX_POINTS);
  if (points.length < METRIC_TREND_MIN_POINTS) {
    return null;
  }
  return {
    dateColumn: x,
    column,
    dates: points.map((point) => point.date),
    values: points.map((point) => point.value),
  };
}
